/**
 * Extracts a searchable NDC/product string from a raw barcode value.
 *
 * Used for both camera-decoded barcode values (via @zxing) and raw text typed by a
 * hardware/HID barcode scanner. Hardware scanners behave like a keyboard: they type the
 * decoded value directly into whatever input has focus, usually followed by an Enter
 * keystroke, so the same parsing logic is reused for that path.
 *
 * Pharmaceutical barcodes:
 *  - UPC-A (12 digits, starts with 0): 0 + [10-digit NDC] + check → strip first and last
 *  - EAN-13 (13 digits, starts with 00): 00 + [10-digit NDC] + check → strip first two and last
 *  - GS1-128 / GS1 DataMatrix (DSCSA unit-of-sale barcodes): element string starting with
 *    AI (01) GTIN-14, optionally followed by other AIs such as (21) serial, (17) expiry,
 *    (10) lot — with or without FNC1/GS separators or parentheses, e.g.
 *    "0100329300350050215C03BDV6XD1727043010GAQH24054" (raw scanner output) or
 *    "(01)00329300350050(17)270430(10)GAQH24054" (human-readable form).
 *    The GTIN is always fixed-length (14 digits), so it can be pulled out unambiguously
 *    regardless of what other variable-length AIs follow it or how they're separated.
 *  - Code 128 / raw NDC digits, or formatted NDC (with dashes): use as-is
 */
export function extractSearchTerm(raw: string): string {
  const trimmed = raw
    .trim()
    // Strip AIM symbology identifier prefixes some scanners prepend (e.g. "]d2", "]e0", "]Q1", "]C1")
    .replace(/^\][A-Za-z0-9]\d/, "")
    // Strip GS1 FNC1 / group-separator control characters some scanners pass through literally
    .replace(/[\u001d]/g, "");

  // GS1 element string – GTIN (AI 01) is always fixed-length 14 digits, so it can be
  // extracted unambiguously even when concatenated directly with other AIs (21, 17, 10, ...)
  // with no separators, or written in bracketed human-readable form "(01)...".
  const gs1Match = trimmed.match(/^\(?01\)?(\d{14})/);
  if (gs1Match) {
    const gtin14 = gs1Match[1];
    // GTIN-14 → strip leading indicator + check digit → 12 → strip leading 0 → 11 → strip check → 10-digit NDC
    return gtin14.slice(3, 13);
  }

  const onlyDigits = trimmed.replace(/[^0-9]/g, "");

  // UPC-A (12 digits, leading 0)
  if (onlyDigits.length === 12 && onlyDigits[0] === "0") {
    return onlyDigits.slice(1, 11); // 10-digit NDC
  }
  // EAN-13 (13 digits, leading 00)
  if (onlyDigits.length === 13 && onlyDigits.startsWith("00")) {
    return onlyDigits.slice(2, 12); // 10-digit NDC
  }
  // GTIN-14 (14 digits)
  if (onlyDigits.length === 14) {
    return onlyDigits.slice(3, 13); // 10-digit NDC
  }

  // 10-digit plain NDC, formatted NDC with dashes, or anything else — use raw
  return trimmed;
}
